import type { RepositoryAccessInput } from '../../api';
import { httpsCloneUrl } from './useGitRepositories';
import type { GitRepositories } from './useGitRepositories';

/** Whether the project deploys code from Git, or only monitors and patches. */
export type RepositoryChoice = 'connect' | 'none';

/** What the repository part of the form currently says. Only this — never a
 * value left over from an earlier choice — reaches the API. */
export interface RepositorySelection {
  choice: RepositoryChoice;
  /** Typed clone URL instead of a repository picked from a connection. */
  manual: boolean;
  manualUrl: string;
  /** owner/name of the repository picked from the connection's list. */
  repository: string;
  branch: string;
}

/**
 * repositoryFields turns the selection into the create-project fields.
 * Shared by the WordPress and Magento onboarding pages (CO-69, CO-73).
 * A picked repository is recorded by its HTTPS clone URL, the form every
 * deployment's checkout authenticates with the connection, and carries the
 * connection it was listed from so the API can verify and store it. A
 * typed URL is recorded as typed; its access is set up on the project.
 * Without a repository nothing repository-related is sent.
 */
export function repositoryFields(
  selection: RepositorySelection,
  git: Pick<GitRepositories, 'provider' | 'connectionKind' | 'providerAccounts' | 'providerTokens'>,
): { repository_url: string; default_branch?: string; repository_access?: RepositoryAccessInput } {
  if (selection.choice === 'none') return { repository_url: '' };
  const branch = selection.branch.trim() || undefined;
  if (selection.manual) return { repository_url: selection.manualUrl.trim(), default_branch: branch };
  const { provider } = git;
  let access: RepositoryAccessInput;
  if (git.connectionKind === 'github_app') {
    access = { method: 'github_app' };
  } else {
    const account = git.providerAccounts[provider] || selection.repository.split('/')[0] || '';
    access = { method: 'token', provider, account, token: git.providerTokens[provider] };
  }
  return { repository_url: httpsCloneUrl(provider, selection.repository), default_branch: branch, repository_access: access };
}
